// reducers/applicationSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { showError, showSuccess } from "../../helpers/toast";
import axios from "axios";
import _ from "lodash";
import qs from "qs";
import moment from "moment";
import { createNotification } from "./notificationSlice";
import { getNotificationTemplate } from "../../utility/notificationTemplates";

const API = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL,
});

API.interceptors.response.use(
  (response) => {
    return Promise.resolve(response);
  },
  (error) => {
    showError(error.response?.data?.error ?? error.message);
    return Promise.reject(error);
  }
);

const initialState = {
  fetchLoading: false,
  fetchError: null,
  message: null,
  data: [],
  allApplications: [], // Store all applications for filtering/counting purposes
  currentApp: {},
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  },
  counts: {
    newApplications: 0,
    pendingFromDW: 0,
    discrepencies: 0,
    approved: 0,
    renewal: 0,
  },
  countsLoading: false,
  countsError: null,
  fallbackAttempted: false,
  auditTrail: {
    logs: [],
    loading: false,
    error: null,
    pagination: {
      currentPage: 1,
      totalPages: 1,
      totalItems: 0,
    },
  },
};

export const updateApplication = createAsyncThunk(
  "updateApplication",
  async (data, { dispatch, getState }) => {
    try {
      const config = {
        headers: {
          "ngrok-skip-browser-warning": "69420",
          "Content-Type": "application/json",
          Authorization: `${data.accessToken}`,
        },
      };

      const payload = {};
      if (data?.state) payload.state_id = data?.state;
      if (data?.json_data) payload.json_data = data?.json_data;
      if (data?.dueDate) payload.dueDate = data.dueDate;
      if (data?.descripencie) payload.descripencie = data.descripencie;
      if (data?.htmlForm) payload.htmlForm = data?.htmlForm;
      if (data?.remarks) payload.remarks = data.remarks;

      const response = await API.put(
        `/applications/${data?.id}`,
        payload,
        config
      );
      showSuccess("Your application has been updated!");

      // Notify Admin if the updater is a Company Admin (Application Owner)
      const state = getState();
      const currentUser = state.auth.user?.user;

      // If user has a company_id, they are likely a Company Admin, not Super Admin
      if (currentUser && currentUser.company_id) {
        const template = getNotificationTemplate('pending', {
          ...data,
          userName: currentUser.full_name || currentUser.first_name || 'User',
          id: data.id
        });

        dispatch(
          createNotification({
            message: `Application updated by ${currentUser.full_name || currentUser.first_name || 'User'}`,
            userId: currentUser.id,
            accessToken: data.accessToken,
            is_admin: true,
          })
        );
      }

      return response.data;
    } catch (error) {
      throw new Error("Failed to load applications");
    }
  }
);

export const fetchApplications = createAsyncThunk(
  "fetchApplications",
  async (data) => {
    try {
      const config = {
        headers: {
          "ngrok-skip-browser-warning": "69420",
          "Content-Type": "application/json",
          Authorization: `${data.accessToken}`,
        },
      };
      let url = "/applications";
      if (data?.queryParams && !_.isEmpty(data?.queryParams)) {
        url = `${url}?${data?.queryParams instanceof Object
          ? qs.stringify(data?.queryParams)
          : data?.queryParams
          }`;
      }
      const response = await API.get(url, config);
      return response.data;
    } catch (error) {
      console.error("fetchApplications error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        url: error.config?.url,
        headers: error.config?.headers
      });
      throw new Error(`Failed to load applications: ${error.response?.data?.message || error.message}`);
    }
  }
);
export const fetchApplicationWithId = createAsyncThunk(
  "fetchApplicationWithId",
  async (data) => {
    try {
      const config = {
        headers: {
          "ngrok-skip-browser-warning": "69420",
          "Content-Type": "application/json",
          Authorization: `${data.accessToken}`,
        },
      };
      const response = await API.get(`/applications/${data?.id}`, config);
      return response.data;
    } catch (error) {
      throw new Error("Failed to load applications");
    }
  }
);

export const fetchApplicationAuditTrail = createAsyncThunk(
  "fetchApplicationAuditTrail",
  async ({ accessToken, id, page = 1, limit = 20, action }) => {
    try {
      const config = {
        headers: {
          "ngrok-skip-browser-warning": "69420",
          "Content-Type": "application/json",
          Authorization: `${accessToken}`,
        },
      };
      const query = { page, limit };
      if (action) query.action = action;
      const response = await API.get(
        `/applications/${id}/audit-trail?${qs.stringify(query)}`,
        config
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Failed to load audit trail"
      );
    }
  }
);
export const postForm = createAsyncThunk("postForms", async (data) => {
  try {
    const config = {
      headers: {
        "ngrok-skip-browser-warning": "69420",
        "Content-Type": "application/json",
        Authorization: `${data.accessToken}`,
      },
    };

    const response = await API.post("/forms", { ...data?.data }, config);
    showSuccess("Your application has been created!");
    return response.data;
  } catch (error) {
    throw new Error("Failed to submit form");
  }
});
export const updateForm = createAsyncThunk("updateForm", async (data, id) => {
  try {
    const config = {
      headers: {
        "ngrok-skip-browser-warning": "69420",
        "Content-Type": "application/json",
        Authorization: `${data.accessToken}`,
      },
    };
    const response = await API.put(
      `/forms/${data?.id}`,
      { ...data?.data },
      config
    );
    showSuccess("Your application has been updated!");

    return response.data;
  } catch (error) {
    throw new Error("Failed to submit form");
  }
});
export const verifyApplicationName = createAsyncThunk(
  "verifyApplicationName",
  async (data) => {
    const config = {
      headers: {
        "ngrok-skip-browser-warning": "69420",
        "Content-Type": "application/json",
        Authorization: `${data.accessToken}`,
      },
    };
    return API.post("/forms/verify-application-name", { ...data }, config);
  }
);

export const addCertificateAndRenewal = (data) => async (dispatch) => {
  try {
    const formData = new FormData();
    formData.append("issue_date", data.issue_date);
    formData.append("due_date", data.due_date);
    formData.append("user_id", data.user_id);
    if (data.file) formData.append("file", data.file);

    const response = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}applications/${data.id}/certificate&renewal`,
      {
        method: "PUT",
        headers: {
          "ngrok-skip-browser-warning": "69420",
          Authorization: `${data.authToken}`,
        },
        body: formData,
      }
    );

    if (!response.ok) throw new Error("Failed to update certificate and renewal");

    const responseData = await response.json();

    dispatch({ type: "CERTIFICATE_UPLOADED", payload: responseData });
  } catch (error) {
    dispatch({ type: "CERTIFICATE_UPLOAD_FAILED", payload: error.message });
  }
};

export const getAllCertificates = async (data) => {
  try {
    // Call the API
    const response = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}applications/users/certificates`,
      {
        method: "GET",
        headers: {
          "ngrok-skip-browser-warning": "69420",
          Authorization: `${data.authToken}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error("Failed to Get Certificates");
    }

    const responseData = await response.json();
    return responseData;
  } catch (error) {
    console.error("Error Getting data:", error);
  }
};

export const fetchV2Applications = async (data) => {
  try {
    const { accessToken, ...queryParams } = data;

    // Filter out undefined or null query parameters
    const filteredParams = Object.fromEntries(
      Object.entries(queryParams).filter(([key, value]) => value !== undefined && value !== null)
    );

    // Prepare the query parameters string if any valid query parameters are available
    const queryString = new URLSearchParams(filteredParams).toString();
    const url = queryString ? `${process.env.REACT_APP_BACKEND_URL}applications?${queryString}` : `${process.env.REACT_APP_BACKEND_URL}applications`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        "Content-Type": "application/json",
        Authorization: accessToken, // Add 'Bearer' if required by your backend
      },
    });

    // Check for successful response
    if (!response.ok) {
      throw new Error("Failed to load applications");
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error("Error fetching applications:", error);
    throw new Error("Failed to load applications");
  }
};

/**
 * Fetches application counts by status from the server
 * 
 * NOTE: This endpoint doesn't exist on the backend yet, so we skip the API call
 * and go directly to the fallback method to avoid 500 errors in console.
 * 
 * When the backend implements this endpoint, update this function to make the actual API call.
 * 
 * Expected API response format:
 * {
 *   "in_process": 5,
 *   "pending_for_approval": 3,
 *   "discrepancies": 2,
 *   "approved": 10,
 *   "renewal": 1
 * }
 */
export const fetchApplicationCounts = createAsyncThunk(
  "fetchApplicationCounts",
  async (data) => {
    // Since the counts API doesn't exist yet, we'll skip it entirely and go straight to fallback
    // This prevents the 500 error from appearing in console
    throw new Error("COUNTS_API_NOT_AVAILABLE");
  }
);

/**
 * Fetches all applications without pagination to calculate accurate total counts
 * This is used as a fallback when the counts API endpoint is not available
 */
export const fetchAllApplicationsForCounts = createAsyncThunk(
  "fetchAllApplicationsForCounts",
  async (data) => {
    try {
      const config = {
        headers: {
          "ngrok-skip-browser-warning": "69420",
          "Content-Type": "application/json",
          Authorization: `${data.accessToken}`,
        },
      };

      const validStates = [
        "in_process",
        "pending",
        "approved",
        "rejected",
        "discrepancies",
        "pending_for_approval",
        "drafts",
        "renewal"
      ];

      let allApplications = [];
      let currentPage = 1;
      let hasMorePages = true;
      const pageSize = 1000; // Fetch 1000 at a time

      // Fetch all pages of applications
      while (hasMorePages) {
        const queryParams = {
          page: currentPage,
          limit: pageSize,
          // state: validStates // Fetch ALL states, not just specific ones
        };

        const queryString = qs.stringify(queryParams);
        const url = `/applications?${queryString}`;

        const response = await API.get(url, config);
        const responseData = response.data;

        // Extract applications from response (handle different response formats)
        let pageApplications = [];
        if (Array.isArray(responseData)) {
          pageApplications = responseData;
        } else if (responseData?.data) {
          pageApplications = Array.isArray(responseData.data) ? responseData.data : [];
        } else if (responseData?.applications) {
          pageApplications = Array.isArray(responseData.applications) ? responseData.applications : [];
        }

        allApplications = allApplications.concat(pageApplications);

        // Check if there are more pages
        if (responseData?.pagination) {
          const totalPages = responseData.pagination.total_pages || responseData.pagination.totalPages || 1;
          hasMorePages = currentPage < totalPages;
          currentPage++;
        } else if (pageApplications.length < pageSize) {
          // If we got fewer items than requested, we've reached the end
          hasMorePages = false;
        } else {
          // If we got a full page, try the next page
          currentPage++;
          // Safety limit: don't fetch more than 10 pages (10,000 records)
          if (currentPage > 10) {
            console.warn('Reached safety limit of 10 pages for counts fetch');
            hasMorePages = false;
          }
        }
      }

      return allApplications;
    } catch (error) {
      console.error("fetchAllApplicationsForCounts error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        url: error.config?.url
      });
      throw new Error(`Failed to load all applications for counts: ${error.response?.data?.message || error.message}`);
    }
  }
);

const applicationSlice = createSlice({
  name: "applications",
  initialState,
  reducers: {
    clearApplications: (state) => {
      state.data = [];
      state.pagination = {
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 10,
      };
      state.fetchError = null;
    },
    clearAuditTrail: (state) => {
      state.auditTrail = {
        logs: [],
        loading: false,
        error: null,
        pagination: {
          currentPage: 1,
          totalPages: 1,
          totalItems: 0,
        },
      };
    },
    resetCountsState: (state) => {
      state.counts = {
        newApplications: 0,
        pendingFromDW: 0,
        discrepencies: 0,
        approved: 0,
        renewal: 0,
      };
      state.countsLoading = false;
      state.countsError = null;
      state.fallbackAttempted = false;
      state.allApplications = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchApplications.pending, (state) => {
        state.fetchLoading = true;
      })
      .addCase(fetchApplications.fulfilled, (state, action) => {
        state.fetchLoading = false;
        // Handle both array response and paginated response
        if (Array.isArray(action.payload)) {
          state.data = action.payload;
          // For array responses, calculate pagination based on length
          const totalPages = Math.ceil(action.payload.length / 10);
          state.pagination = {
            currentPage: 1,
            totalPages: totalPages || 1,
            totalItems: action.payload.length,
            itemsPerPage: 10,
          };
        } else {
          state.data = action.payload.data || action.payload.applications || [];
          // Update pagination metadata if available
          if (action.payload.pagination) {
            state.pagination = {
              currentPage: action.payload.pagination.current_page || action.payload.pagination.page || 1,
              totalPages: action.payload.pagination.total_pages || action.payload.pagination.totalPages || 1,
              totalItems: action.payload.pagination.total || action.payload.pagination.totalItems || 0,
              itemsPerPage: action.payload.pagination.per_page || action.payload.pagination.limit || 10,
            };
          } else if (action.payload.currentPage && action.payload.totalPages) {
            // Handle direct pagination metadata in response (your API format)
            state.pagination = {
              currentPage: action.payload.currentPage || 1,
              totalPages: action.payload.totalPages || 1,
              totalItems: action.payload.total || 0,
              itemsPerPage: 10,
            };
          } else {
            // Fallback pagination if no metadata provided
            const totalPages = Math.ceil(state.data.length / 10);
            state.pagination = {
              currentPage: 1,
              totalPages: totalPages || 1,
              totalItems: state.data.length,
              itemsPerPage: 10,
            };
          }
        }
        state.fetchError = null;
      })
      .addCase(fetchApplications.rejected, (state, action) => {
        state.fetchLoading = false;
        state.fetchError = action.error.message;
        state.data = null;
        state.message = null;
      })
      .addCase(fetchApplicationWithId.pending, (state) => {
        state.fetchLoading = true;
        state.currentApp = {};
      })
      .addCase(fetchApplicationWithId.fulfilled, (state, action) => {
        state.fetchLoading = false;
        state.currentApp = action.payload;
        state.fetchError = null;
      })
      .addCase(fetchApplicationWithId.rejected, (state, action) => {
        state.fetchLoading = false;
        state.fetchError = action.error.message;
        state.currentApp = null;
        state.message = null;
      })
      .addCase(fetchApplicationCounts.pending, (state) => {
        state.countsLoading = true;
        state.countsError = null;
      })
      .addCase(fetchApplicationCounts.fulfilled, (state, action) => {
        state.countsLoading = false;
        state.countsError = null;
        // Map the API response to our expected format
        if (action.payload) {
          state.counts = {
            newApplications: action.payload.in_process || action.payload.newApplications || 0,
            pendingFromDW: action.payload.pending_for_approval || action.payload.pendingFromDW || 0,
            discrepencies: action.payload.discrepancies || action.payload.discrepencies || 0,
            approved: action.payload.approved || 0,
            renewal: action.payload.renewal || 0,
          };
        }
      })
      .addCase(fetchApplicationCounts.rejected, (state, action) => {
        state.countsLoading = false;
        state.countsError = action.error.message;
        // Don't reset counts here, let the fallback mechanism handle it
      })
      .addCase(fetchAllApplicationsForCounts.pending, (state) => {
        state.countsLoading = true;
        state.countsError = null;
        state.fallbackAttempted = true;
      })
      .addCase(fetchAllApplicationsForCounts.fulfilled, (state, action) => {
        state.countsLoading = false;
        state.countsError = null;

        // Calculate counts from all applications
        const applicationsArray = Array.isArray(action.payload)
          ? action.payload
          : action.payload?.applications || action.payload?.data || [];

        // Store all applications for filtering purposes (e.g., company filter)
        state.allApplications = applicationsArray;

        let newApplications = 0;
        let pendingFromDW = 0;
        let discrepencies = 0;
        let approved = 0;
        let renewal = 0;

        applicationsArray.forEach((item) => {
          // Check if application is in "renewal" state OR is "approved" and due within 15 days
          const isDueSoon = item.state === "approved" &&
            item.due_date &&
            moment(item.due_date).isBefore(moment().add(15, 'days')) &&
            moment(item.due_date).isAfter(moment().subtract(1, 'days')); // Ensure it's not way in the past if needed, or just keep it simple

          if (item.state === "in_process") newApplications++;
          else if (item.state === "pending_for_approval") pendingFromDW++;
          else if (item.state === "discrepancies") discrepencies++;
          else if (item.state === "approved") approved++;

          if (item.state === "renewal" || isDueSoon) renewal++;
        });

        const calculatedCounts = {
          newApplications,
          pendingFromDW,
          discrepencies,
          approved,
          renewal,
        };

        state.counts = calculatedCounts;
      })
      .addCase(fetchAllApplicationsForCounts.rejected, (state, action) => {
        state.countsLoading = false;
        state.countsError = action.error.message;
        // Reset counts to default values on error
        state.counts = {
          newApplications: 0,
          pendingFromDW: 0,
          discrepencies: 0,
          approved: 0,
          renewal: 0,
        };
      })
      .addCase(fetchApplicationAuditTrail.pending, (state) => {
        state.auditTrail.loading = true;
        state.auditTrail.error = null;
      })
      .addCase(fetchApplicationAuditTrail.fulfilled, (state, action) => {
        state.auditTrail.loading = false;
        state.auditTrail.logs = action.payload?.logs || [];
        state.auditTrail.pagination = {
          currentPage: action.payload?.currentPage || 1,
          totalPages: action.payload?.totalPages || 1,
          totalItems: action.payload?.total || 0,
        };
        state.auditTrail.error = null;
      })
      .addCase(fetchApplicationAuditTrail.rejected, (state, action) => {
        state.auditTrail.loading = false;
        state.auditTrail.error = action.error.message;
        state.auditTrail.logs = [];
      });
  },
});

export const { clearApplications, resetCountsState, clearAuditTrail } =
  applicationSlice.actions;
export default applicationSlice.reducer;
